import { parseRigs } from '../../game/rigManifest'
import type { RigDef } from '../../game/rigManifest'

/**
 * One runner slot of the cast (PHASE3 T3.1, O-27): built from the art data, so a runner the art
 * session delivers appears in the hub without a code change. `charId` is the slot number N of
 * the manifest kind `player_N`, the id sent on join (D-033).
 */
export interface RunnerSlot {
  charId: number
  /** Manifest kind, `player_N`. */
  kind: string
  /** From `runners.json`; null = no name yet. */
  name: string | null
  /** Placeholder colour from the sprite manifest. */
  color: string
  /** Cutout rig from `rigs.json`, or null. */
  rig: RigDef | null
  /** A single delivered image (manifest `art.idle` / `art.default`) when there is no rig. */
  image: string | null
  /** Has art, so it can be equipped; slots without art show "New runner soon". */
  ready: boolean
}

const KIND_RE = /^player_(\d{1,3})$/
const FALLBACK_COLOR = '#8a97ad'

type Obj = Record<string, unknown>

function asObj(v: unknown): Obj {
  return typeof v === 'object' && v !== null && !Array.isArray(v) ? (v as Obj) : {}
}

function str(v: unknown): string | null {
  return typeof v === 'string' && v !== '' ? v : null
}

/** The manifest entry's single image: `idle`, else `default`, when it is one frame. */
function singleImage(entry: Obj): string | null {
  const art = asObj(entry.art)
  for (const anim of ['idle', 'default']) {
    const a = asObj(art[anim])
    const frames = a.frames ?? 1
    if (str(a.src) && frames === 1) return str(a.src)
  }
  return null
}

/**
 * Runner slots in `charId` order from the sprite manifest (`player_N` entries), the rigs and the
 * name list. A rig without a manifest entry still gets a slot. Bad rig data falls back to no rig
 * (the race keeps its own error path for that).
 */
export function runnerSlots(manifest: unknown, rigsJson: unknown, names: unknown): RunnerSlot[] {
  let rigs: Map<string, RigDef>
  try {
    rigs = parseRigs(rigsJson).runners
  } catch {
    rigs = new Map()
  }
  const sprites = asObj(manifest)
  const kinds = new Set([...Object.keys(sprites), ...rigs.keys()].filter((k) => KIND_RE.test(k)))
  const nameOf = asObj(names)
  return [...kinds]
    .map((kind) => {
      const entry = asObj(sprites[kind])
      const rig = rigs.get(kind) ?? null
      const image = rig ? null : singleImage(entry)
      return {
        charId: Number(KIND_RE.exec(kind)![1]),
        kind,
        name: str(asObj(nameOf[kind]).name),
        color: str(entry.color) ?? FALLBACK_COLOR,
        rig,
        image,
        ready: rig !== null || image !== null,
      }
    })
    .sort((a, b) => a.charId - b.charId)
}

/** The slot to show for `charId`: the stored one if it can be worn, else the first that can. */
export function pickRunner(slots: readonly RunnerSlot[], charId: number): number {
  if (slots.some((s) => s.charId === charId && s.ready)) return charId
  return slots.find((s) => s.ready)?.charId ?? slots[0]?.charId ?? 0
}

/** Display name of a slot: its name, else "Runner N+1". */
export function runnerName(slot: RunnerSlot): string {
  return slot.name ?? `Runner ${slot.charId + 1}`
}
